import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  Button,
} from "@mui/material";
import { GenericDialog } from "../../../generic/GenericDialog";
import { GenericFlag } from "../../../generic/GenericFlag";
import useCountriesQuery from "../../../../hooks/apiHooks/useCountriesQuery";
import { COUNTRY_WITH_CODES } from "../../../../enums/countries";
import useStore from "../../../../states/store";
import { router } from "@inertiajs/react";

export const FilterByLocationDialog = ({ openFilterLocationDialog, handleClose }) => {
  const [, { setCreatorFilters, setPopularTag }] = useStore();

  const { data: countries, isLoading } = useCountriesQuery({
    status: "active",
    role: ["creator", "pending_creator"],
  });

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const countriesWithCode = useMemo(() => {
    if (!countries?.data) return [];
    return countries.data.map((item) => {
      const isoDetails = COUNTRY_WITH_CODES.find((c) => c.name === item.country) || {};
      return {
        ...item,
        name: item.country,
        code: isoDetails.code || "",
        isoCode: isoDetails.isoCode || "",
      };
    });
  }, [countries?.data]);

  const handleCloseFilterLocationDialog = () => {
    setSelectedCountry(null);
    setSelectedStates([]);
    setSelectAll(false);
    handleClose();
  };

  const handleSelectCountry = (event, value) => {
    setSelectedCountry(value);
    setSelectedStates([]);
    setSelectAll(false);
  };

  const handleSelectState = (state) => {
    setSelectedStates((prev) => {
      const newStates = prev.includes(state) ? prev.filter((s) => s !== state) : [...prev, state];

      // Update select All based on whether all states are selected
      setSelectAll(newStates.length === (selectedCountry?.states?.length || 0) && newStates.length > 0);

      return newStates;
    });
  };

  const handleSelectAllStates = () => {
    if (selectAll) {
      setSelectedStates([]);
      setSelectAll(false);
    } else {
      setSelectedStates(selectedCountry?.states?.map((state) => state.state || "Unspecified") || []);
      setSelectAll(true);
    }
  };

  const handleApplyFilter = () => {
    const filters = {};

    if (selectedCountry) {
      filters.country = [selectedCountry.name];

      if (selectedStates.length > 0) {
        filters.state = selectedStates.map((state) => (state === "Unspecified" ? "" : state));
      }
    }
    setCreatorFilters(filters);
    setPopularTag("Location");
    router.visit("/collab-creators");
    handleClose();
  };

  const handleClear = () => {
    setSelectedCountry(null);
    setSelectedStates([]);
    setSelectAll(false);
  };

  return (
    <GenericDialog
      title="Location"
      maxWidth="xsm"
      mb={0}
      openDialog={openFilterLocationDialog}
      handleClose={handleCloseFilterLocationDialog}
    >
      <Typography variant="subtitle1" mb={2}>
        Pick a location to filter and see results for that area.
      </Typography>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 1 }}>
            <Autocomplete
              fullWidth
              options={countriesWithCode}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.isoCode === value.isoCode}
              onChange={handleSelectCountry}
              value={selectedCountry}
              renderOption={(props, item) => (
                <li {...props}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 1, mx: 0 }}>
                    <GenericFlag code={item.isoCode} height="20px" width="25px" />
                    <Typography>{item.name}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Search Countries" />}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <FormControlLabel
              control={<Checkbox checked={selectAll} onChange={handleSelectAllStates} disabled={!selectedCountry} />}
              label="Select All States/Region"
            />
            <Typography>
              {selectedStates.length} of {selectedCountry?.states?.length || 0} selected
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ height: "256px", overflowY: "auto", p: 1 }}>
            {selectedCountry?.states?.length ? (
              selectedCountry.states.map((state, index) => {
                const stateName = state.state || "Unspecified";
                return (
                  <Box key={stateName + "-" + index} sx={{ display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Checkbox checked={selectedStates.includes(stateName)} onChange={() => handleSelectState(stateName)} />
                      }
                      label={stateName}
                    />
                    <Typography variant="body2" color="text.secondary">
                      ({state.total})
                    </Typography>
                  </Box>
                );
              })
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography color="grey.dark">Select a country to view states</Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      <Box display="flex" gap={1} flexDirection="row" justifyContent="center" mt={2}>
        <Button variant="outlined" className="longButton" onClick={handleClear}>
          Clear
        </Button>
        <Button variant="contained" className="longButton" onClick={handleApplyFilter} disabled={!selectedCountry}>
          Apply Filter
        </Button>
      </Box>
    </GenericDialog>
  );
};
